<template>
    <div class="form-group">
        <label :for="id" v-if="!hideLabel">
            {{ labelText }}:
        </label>
        <div class="">
            <input
                v-model="email"
                type="email"
                :id="id"
                class="input"
                :name="name"
                @input="checkEmail"
                @blur="checkEmail"
                required
            />
        </div>
        <p v-if="suggestion" class="email-validation__suggestion-text">
            {{ didYouMeanText }} <strong>{{ suggestion.full }}</strong>?
            <button
                @click="applySuggestion"
                class="email-validation__suggestion-button"
                type="button"
            >
                {{ applyText }}
            </button>
        </p>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import emailSpellChecker, { POPULAR_DOMAINS, POPULAR_TLDS } from '@zootools/email-spell-checker';

const props = defineProps({
    initialValue: {
        type: String,
        default: ''
    },
    labelText: {
        type: String,
        default: 'Email address'
    },
    hideLabel: {
        type: Boolean,
        default: false
    },
    didYouMeanText: {
        type: String,
        default: 'Did you mean'
    },
    applyText: {
        type: String,
        default: 'Apply'
    },
    name: {
        type: String,
        default: 'email_create'
    },
    id: {
        type: String,
        required: true
    }
});

const email      = ref(props.initialValue);
const suggestion = ref(null);

const checkEmail = () => {
    suggestion.value = null;
    if (email.value && email.value.includes('@')) {
        const [localPart, domain] = email.value.split('@');
        let tlds                  = [currentTld.value];

        let domains = [
            'ziggo.nl',
            'telenet.be',
            'skynet.be',
            'gmx.de',
            'web.de',
            'orange.fr',
            'wanadoo.fr',
            'mail.tele.dk',
            'hotmail.com',
            'live.com',
            'outlook.com',
            'gmail.com',
        ];
        domains     = domains.concat(POPULAR_DOMAINS);
        tlds        = tlds.concat(POPULAR_TLDS);

        // Check the full email
        const fullCheck = emailSpellChecker.run({ email: email.value, domains: domains, topLevelDomains: tlds });

        // Check only the domain
        const domainCheck = emailSpellChecker.run({ email: `test@${domain}`, domains: domains, topLevelDomains: tlds });

        // Prioritize the full email check, fall back to domain check if needed
        if (fullCheck && fullCheck.full !== email.value) {
            suggestion.value = fullCheck;
        } else if (domainCheck && domainCheck.domain !== domain) {
            suggestion.value = {
                full: `${localPart}@${domainCheck.domain}`,
                domain: domainCheck.domain
            };
        }
    }
};

const applySuggestion = () => {
    if (suggestion.value) {
        email.value      = suggestion.value.full;
        suggestion.value = null;
    }
};

const currentTld = computed(() => {
    let hostParts = window.location.hostname.split('.');

    switch(window.id_shop) {
        case 1:
            return 'de';
        case 2:
            return 'be';
        case 3:
            return 'fr';
        case 4:
            return 'nl';
        case 5:
            return 'it';
        case 6:
            return 'at';
        case 7:
            return 'dk';
        case 8:
        case 9:
            return 'ch';
        case 10:
            return 'se';
        default: return hostParts.length ? hostParts[hostParts.length - 1] : 'nl';

    }
});

watch(email, () => {
    checkEmail();
});

onMounted(() => {
    email.value = props.initialValue;
    checkEmail();
});
</script>

<style>
.email-validation__suggestion-text {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #000;
}

.email-validation__suggestion-button {
    color: #008000;
    font-weight: 500;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    margin-left: 0.25rem;
}
</style>
